import React, { useState, useEffect } from "react";
import { Gift, Copy, Check, X, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { COLORS } from "../../utils/constants";

/**
 * Bandeau viral incitant l'utilisateur à parrainer.
 * - Fermable (persistance dans localStorage pour 7 jours)
 * - Copie du lien de parrainage en 1 clic
 * - Partage direct via l'API Web Share (mobile) ou copie
 */
export const ReferralInviteBanner = () => {
  const api = useApi();
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    const dismissedAt = localStorage.getItem("biz360_referral_banner_dismissed_at");
    if (dismissedAt) {
      const ms = Date.now() - parseInt(dismissedAt, 10);
      if (ms < 1000 * 60 * 60 * 24 * 7) return; // 7 jours
    }
    setDismissed(false);
    api.get("/referral/my-code").then((r) => setReferral(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (dismissed || !referral?.referral_code) return null;

  const link = referral.referral_link || `https://biz360.obusiness.fr/subscription?ref=${referral.referral_code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Lien copié — partage-le à un(e) ami(e) !");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const share = async () => {
    const shareData = {
      title: "Biz360 — Le copilote de ton auto-entreprise",
      text: "Je t'offre 1 mois gratuit sur Biz360, l'appli qui gère mon Urssaf et ma TVA à ma place 🎁",
      url: link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* ignore */ }
    }
    copy();
  };

  const close = () => {
    localStorage.setItem("biz360_referral_banner_dismissed_at", Date.now().toString());
    setDismissed(true);
  };

  return (
    <div
      className="relative rounded-lg border flex items-center gap-3 px-3 py-2 text-sm"
      style={{
        borderColor: '#E9E5FA',
        background: `linear-gradient(90deg, ${COLORS.accent}10 0%, transparent 70%)`,
      }}
      data-testid="referral-invite-banner"
    >
      <Gift size={16} style={{ color: COLORS.accent }} className="flex-shrink-0" />

      <span className="flex-1 min-w-0 text-xs sm:text-sm truncate">
        <strong style={{ color: COLORS.principale }}>Parraine un ami :</strong>
        <span className="text-muted-foreground"> il gagne 1 mois, toi aussi.</span>
      </span>

      <button
        onClick={share}
        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition hover:opacity-90"
        style={{ backgroundColor: COLORS.principale, color: '#F5E9D2' }}
        data-testid="referral-banner-share"
      >
        <Share2 size={12} /> Partager
      </button>

      <button
        type="button"
        onClick={close}
        aria-label="Masquer"
        className="p-1 rounded hover:bg-black/5 transition text-muted-foreground"
        data-testid="referral-banner-close"
      >
        <X size={14} />
      </button>
    </div>
  );
};
